import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import RoleService, { CreateRoleRequest, UpdateRoleRequest } from '../services/role.service';

// The Users page lists the roles under the key "roles" too, so one invalidation refreshes both
const ROLES_KEY = ['roles'];

export const useRoleList = () => {
  return useQuery({
    queryKey: [...ROLES_KEY, 'list'],
    queryFn: () => RoleService.getAllRoles(),
  });
};

export const useRoleSummary = () => {
  return useQuery({
    queryKey: [...ROLES_KEY, 'summary'],
    queryFn: () => RoleService.getSummary(),
  });
};

// The areas and actions a role can be allowed; fixed while the application is open
export const usePermissionCatalogue = () => {
  return useQuery({
    queryKey: [...ROLES_KEY, 'catalogue'],
    queryFn: () => RoleService.getCatalogue(),
    staleTime: Infinity,
  });
};

export const useRoleDetail = (id: string | null) => {
  return useQuery({
    queryKey: [...ROLES_KEY, 'detail', id],
    queryFn: () => RoleService.getRoleById(id as string),
    enabled: !!id,
  });
};

const useInvalidateRoles = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ROLES_KEY });
    // User lists show the role of each user
    queryClient.invalidateQueries({ queryKey: ['users'] });
    // The signed-in user's own role may be the one that changed
    window.dispatchEvent(new Event('auth:forbidden'));
  };
};

export const useCreateRole = () => {
  const invalidate = useInvalidateRoles();
  return useMutation({
    mutationFn: (data: CreateRoleRequest) => RoleService.createRole(data),
    onSuccess: invalidate,
  });
};

export const useUpdateRole = () => {
  const invalidate = useInvalidateRoles();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRoleRequest }) => RoleService.updateRole(id, data),
    onSuccess: invalidate,
  });
};

export const useCloneRole = () => {
  const invalidate = useInvalidateRoles();
  return useMutation({
    mutationFn: ({ id, displayName }: { id: string; displayName: string }) => RoleService.cloneRole(id, displayName),
    onSuccess: invalidate,
  });
};

export const useDeleteRole = () => {
  const invalidate = useInvalidateRoles();
  return useMutation({
    mutationFn: (id: string) => RoleService.deleteRole(id),
    onSuccess: invalidate,
  });
};
